import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A GeometryInstance3D the previewer does not draw. It mounts the Node3D transform, and holds the
 * node's place in the scene cull, so its visibility range still decides its visibility dependants.
 * Each type gives the box its Godot base gives the instance.
 */
import { useMemo, useRef } from 'react';
import { Node3D } from '../../base/node3d/Component';
import { boxPlacement } from '../../../r3f/visibilityRange/placements';
import { PluginGizmo } from '../../../r3f/gizmo/PluginGizmo';
import { invisibleBase } from '../../../r3f/invisibleBase';
import { useGeometryInstance } from '../../../r3f/visibilityRange/geometryInstance';
import { EMPTY_AABB } from '../../../godot/math/aabb';
import { FrameAabb } from '../../../r3f/components/NodeAabb';
const sameAsOwn = (ownAabb) => ownAabb;
/** `get_aabb()` is `AABB()`: a point at the node origin, whatever box the instance culls by. */
export const emptyFrameAabb = () => EMPTY_AABB;
/** The component an undrawn GeometryInstance3D type registers, culled by the box `useOwnAabb` gives. */
export function undrawnGeometryInstance(useOwnAabb, frameAabbOf = sameAsOwn) {
    function UndrawnGeometryInstance({ node, children }) {
        const nodeRef = useRef(null);
        const ownAabb = useOwnAabb(node);
        const frameAabb = frameAabbOf(ownAabb);
        const placement = useMemo(() => boxPlacement(nodeRef, ownAabb), [ownAabb]);
        useGeometryInstance(placement);
        return (_jsxs(Node3D, { node: node, ref: nodeRef, children: [frameAabb && _jsx(FrameAabb, { aabb: frameAabb }), children] }));
    }
    return invisibleBase(UndrawnGeometryInstance);
}
/**
 * An undrawn GeometryInstance3D that carries its editor gizmos (ADR-0056). `drawnFor` says which
 * nodes Godot draws the gizmo plugin for. `Gizmos` is mounted for those nodes, and mounts the parts
 * that only a selected gizmo has in a selected-only `PluginGizmo` of their own.
 */
export function undrawnGeometryInstanceWithGizmos(useOwnAabb, frameAabbOf, Gizmos, drawnFor) {
    const Undrawn = undrawnGeometryInstance(useOwnAabb, frameAabbOf);
    function UndrawnGeometryInstanceWithGizmos({ node, children }) {
        return (_jsxs(Undrawn, { node: node, children: [_jsx(PluginGizmo, { drawnFor: drawnFor, children: _jsx(Gizmos, { properties: node.properties }) }), children] }));
    }
    return invisibleBase(UndrawnGeometryInstanceWithGizmos);
}
/**
 * A bare GeometryInstance3D gives its instance no base (`godot/scene/geometryBase.ts`), so no box to
 * cull by, and `get_aabb()` is the unset virtual's `AABB()`, a point to frame on.
 */
export const GeometryInstance3D = undrawnGeometryInstance(() => null, emptyFrameAabb);
//# sourceMappingURL=Component.js.map